import { Tabs, TabsContent, TabsList, TabsTrigger } from '@radix-ui/react-tabs'
import clsx from 'clsx'
import { ArrowRightIcon, CircleCheckIcon } from 'lucide-react'
import { useState } from 'react'

import { Slider } from 'components/ui/slider'
import { Button } from 'slab'

import { emitEmployerOnboardingEvent } from '../../utils'
import { COVERAGE_LEVEL_OPTIONS } from '../consts'
import { useSelfServeExplorerContext } from '../QuoteSelfServeExplorerContext'
import { type CoverageLevel } from '../types'

interface SmartCoverageAmountStepProps {
  onBack: () => void
  onNext: () => void
}

export function SmartCoverageAmountStep ({ onBack, onNext }: SmartCoverageAmountStepProps) {
  const {
    filterValues,
    setFilterValues,
    selectedCoverageLevel,
    onSelectedCoverageLevelChange,
    coverageLevelPills,
    hasLowestCostBenchmark,
    hasBetterBenefitsBenchmark,
  } = useSelfServeExplorerContext()

  const [activeTab, setActiveTab] = useState('recommended')

  const isOptionDisabled = (optionValue: CoverageLevel) => {
    if (optionValue === 'lowest_cost') return !hasLowestCostBenchmark
    if (optionValue === 'better_benefits') return !hasBetterBenefitsBenchmark
    return false
  }

  // Map coverage level to slider value
  const getCoverageLevelSliderValue = (level: CoverageLevel): number => {
    switch (level) {
      case 'lowest_cost': return 33
      case 'best_fit': return 66
      case 'better_benefits': return 100
      default: return 66
    }
  }

  // Map slider value to coverage level
  const getSliderValueCoverageLevel = (value: number): CoverageLevel => {
    if (value <= 33) return 'lowest_cost'
    if (value <= 66) return 'best_fit'
    return 'better_benefits'
  }

  const handleCoverageLevelChange = (level: CoverageLevel) => {
    onSelectedCoverageLevelChange(level)
    setFilterValues({
      ...filterValues,
      coverageLevel: level,
      employerEmployeeContribution: getCoverageLevelSliderValue(level) / 100,
    })

    if (level === 'lowest_cost') {
      emitEmployerOnboardingEvent('[ER] AllowanceTool coverage lowest cost selected', {})
    } else if (level === 'best_fit') {
      emitEmployerOnboardingEvent('[ER] AllowanceTool coverage best fit selected', {})
    } else if (level === 'better_benefits') {
      emitEmployerOnboardingEvent('[ER] AllowanceTool coverage better benefits selected', {})
    }
  }

  const handleSliderChange = (value: number[]) => {
    const newValue = value[0]
    const newLevel = getSliderValueCoverageLevel(newValue)

    // Update coverage level if it changed
    if (newLevel !== selectedCoverageLevel && !isOptionDisabled(newLevel)) {
      onSelectedCoverageLevelChange(newLevel)
      setFilterValues({
        ...filterValues,
        coverageLevel: newLevel,
        employerEmployeeContribution: newValue / 100
      })
    } else {
      // Just update contribution value
      setFilterValues({ ...filterValues, employerEmployeeContribution: newValue / 100 })
    }

    emitEmployerOnboardingEvent('[ER] AllowanceTool custom cost sharing updated', {})
  }

  const selectedOption = COVERAGE_LEVEL_OPTIONS.find(opt => opt.value === selectedCoverageLevel)

  return (
    <div className='flex flex-col gap-4 p-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h2 className='heading-sm text-gray-800'>Coverage amount</h2>
          <p className='body text-gray-500 mt-1'>
            The coverage amount will help determine your employees' allowances.
          </p>
        </div>
        <div className='flex gap-2'>
          <Button variant='secondary' size='small' onClick={onBack}>
            Back
          </Button>
          <Button variant='primary' size='small' onClick={onNext} rightIcon={ArrowRightIcon}>
            Next
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className='flex flex-row gap-4 border-b border-gray-200'>
          <TabsTrigger
            value='recommended'
            className='text-gray-800 body data-[state=active]:body-bold data-[state=active]:border-b-2 border-gray-800 p-2'
          >
            Recommended
          </TabsTrigger>
          <TabsTrigger
            value='additional'
            className='text-gray-800 body data-[state=active]:body-bold data-[state=active]:border-b-2 border-gray-800 p-2'
          >
            Additional settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value='recommended' className='pt-6'>
          <div className='flex flex-col gap-4'>
            <div className='flex rounded-2xl border border-gray-200 bg-white p-1'>
              {COVERAGE_LEVEL_OPTIONS.map((opt) => {
                const isDisabled = isOptionDisabled(opt.value)
                const isSelected = selectedCoverageLevel === opt.value
                return (
                  <button
                    key={opt.value}
                    type='button'
                    disabled={isDisabled}
                    className={clsx(
                      'flex-1 text-center',
                      'flex items-center justify-center px-4 py-2',
                      'body rounded-xl border',
                      'transition-colors duration-200',
                      isSelected
                        ? 'bg-thatch-100 border-thatch-500 text-gray-800 font-semibold'
                        : 'border-transparent',
                      !isSelected && isDisabled && 'bg-transparent text-gray-400 cursor-not-allowed',
                      !isSelected && !isDisabled && 'bg-transparent text-gray-700 hover:bg-gray-50'
                    )}
                    onClick={() => !isDisabled && handleCoverageLevelChange(opt.value)}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>

            {selectedOption && (
              <div className='text-center'>
                <p className='body text-gray-600'>{selectedOption.description}</p>
                {coverageLevelPills && coverageLevelPills.length > 0 && (
                  <div className='flex flex-wrap gap-2 justify-center mt-3'>
                    {coverageLevelPills.map((pill, idx) => (
                      <span
                        key={idx}
                        className='inline-flex items-center gap-1 px-3 py-1 text-sm rounded-full bg-green-50/60 text-green-700'
                      >
                        <CircleCheckIcon className='w-4 h-4 text-green-500' />
                        {pill.text}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value='additional' className='pt-6'>
          <div className='flex flex-col gap-4'>
            <div className='flex rounded-2xl border border-gray-200 bg-white p-1'>
              {COVERAGE_LEVEL_OPTIONS.map((opt) => {
                const isDisabled = isOptionDisabled(opt.value)
                const isSelected = selectedCoverageLevel === opt.value
                return (
                  <button
                    key={opt.value}
                    type='button'
                    disabled={isDisabled}
                    className={clsx(
                      'flex-1 text-center',
                      'flex items-center justify-center px-4 py-2',
                      'body rounded-xl border',
                      'transition-colors duration-200',
                      isSelected
                        ? 'bg-thatch-100 border-thatch-500 text-gray-800 font-semibold'
                        : 'border-transparent',
                      !isSelected && isDisabled && 'bg-transparent text-gray-400 cursor-not-allowed',
                      !isSelected && !isDisabled && 'bg-transparent text-gray-700 hover:bg-gray-50'
                    )}
                    onClick={() => !isDisabled && handleCoverageLevelChange(opt.value)}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>

            {selectedOption && (
              <div className='text-center'>
                <p className='body text-gray-600'>{selectedOption.description}</p>
                {coverageLevelPills && coverageLevelPills.length > 0 && (
                  <div className='flex flex-wrap gap-2 justify-center mt-3'>
                    {coverageLevelPills.map((pill, idx) => (
                      <span
                        key={idx}
                        className='inline-flex items-center gap-1 px-3 py-1 text-sm rounded-full bg-green-50/60 text-green-700'
                      >
                        <CircleCheckIcon className='w-4 h-4 text-green-500' />
                        {pill.text}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className='flex flex-col gap-4 pt-2'>
              <div className='flex items-center justify-between'>
                <span className='body-bold text-gray-800'>Cost sharing</span>
                <div className='flex items-center gap-4 text-sm'>
                  <div className='flex items-center gap-2'>
                    <span className='w-2 h-2 rounded-full bg-gray-800' />
                    <span className='text-gray-600'>
                      <span className='font-semibold'>{Math.round(filterValues.employerEmployeeContribution * 100)}%</span> Your contribution
                    </span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <span className='w-2 h-2 rounded-full bg-gray-300' />
                    <span className='text-gray-600'>
                      <span className='font-semibold'>{Math.round((1 - filterValues.employerEmployeeContribution) * 100)}%</span> Your employees' contribution
                    </span>
                  </div>
                </div>
              </div>
              <div className='flex items-center gap-4'>
                <span className='body text-gray-500 w-8'>0%</span>
                <div className='flex-1'>
                  <Slider
                    min={0}
                    max={100}
                    value={[filterValues.employerEmployeeContribution * 100]}
                    trackColor='bg-gray-300'
                    rangeColor='bg-gray-800'
                    onValueChange={handleSliderChange}
                  />
                </div>
                <span className='body text-gray-500 w-10'>100%</span>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
